/**
 * What to say — and when to say it — when an action that needs the server cannot reach it.
 *
 * Creating a team or a tournament is not something the phone can do on its own, the way scoring
 * a ball is: the server issues the ids. So with no signal the honest answer is "this needs a
 * connection", said in the same words as creating a match, rather than a raw browser error
 * ("Failed to fetch") or a "please try again" that gives no reason to.
 */
import { ApiRequestError } from "../api.ts";

/**
 * Whether a failure means the server never really answered.
 *
 * No response at all, or a 5xx. A 401, 403, 409 or 422 *is* an answer — the server heard and
 * said no — and dressing that up as "no signal" would send a scorer to look for a network
 * problem that is not there.
 */
export function isUnreachable(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  return error.status >= 500;
}

/** Shown as soon as the page opens with no connection, before anything is tapped. */
export const offlineNotice = (what: string): string =>
  `You are offline. ${what} needs a connection — connect, then create it.`;

export const connectionMessage = (what: string): string =>
  `${what} needs a connection. What you entered is still here — try again when you have signal.`;
